import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarPlus, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  draft: { label: 'Borrador', cls: 'bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]' },
  review: { label: 'En revisión', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  published: { label: 'Publicado', cls: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'Rechazado', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const MyCreatedEvents = () => {
  const [events, setEvents] = useState([]);
  const [submitting, setSubmitting] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/mine`, { withCredentials: true });
      setEvents(res.data || []);
    } catch {
      /* silent — section simply stays hidden */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForReview = async (eventId) => {
    setSubmitting(eventId);
    try {
      const res = await axios.post(`${API}/events/${eventId}/submit`, {}, { withCredentials: true });
      toast.success(res.data?.status === 'published'
        ? '¡Evento publicado!'
        : 'Evento enviado a revisión. Un administrador lo aprobará pronto.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar a revisión');
    } finally {
      setSubmitting(null);
    }
  };

  if (events.length === 0) return null;

  return (
    <div className="bg-white/50 backdrop-blur-sm border border-[#E5E5E5] rounded-lg p-6" data-testid="my-created-events">
      <div className="flex items-center gap-2 mb-4">
        <CalendarPlus className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-semibold text-[#0A0A0A]">Mis eventos creados</h2>
      </div>
      <div className="space-y-3">
        {events.map((ev) => {
          const st = STATUS[ev.status] || STATUS.draft;
          return (
            <div key={ev.event_id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-white rounded-lg border border-[#E5E5E5]" data-testid={`my-event-${ev.event_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0A0A0A] truncate">{ev.title}</p>
                <p className="text-sm text-[#737373]">
                  {format(new Date(ev.start_date), "d 'de' MMMM, yyyy · HH:mm 'hs'", { locale: es })}
                </p>
              </div>
              <span className={`inline-block self-start sm:self-center text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${st.cls}`} data-testid={`my-event-status-${ev.event_id}`}>
                {st.label}
              </span>
              <div className="flex gap-2">
                {ev.status === 'draft' && (
                  <Button
                    size="sm"
                    onClick={() => submitForReview(ev.event_id)}
                    disabled={submitting === ev.event_id}
                    className="bg-primary hover:bg-primary/90 text-white"
                    data-testid={`submit-event-${ev.event_id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {submitting === ev.event_id ? 'Enviando...' : 'Enviar a revisión'}
                  </Button>
                )}
                {ev.status === 'published' && (
                  <Link to={`/eventos/${ev.event_id}`}>
                    <Button size="sm" variant="outline" className="border-[#E5E5E5] text-[#525252]" data-testid={`view-event-${ev.event_id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1.5" /> Ver
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyCreatedEvents;
